/**
 * The cross-section: any measure against any other, across the region.
 *
 * Pure — no React, no fetching. This is the module worth checking numerically,
 * because everything the panel claims is computed here.
 *
 * The statistics are deliberately modest. Pearson and Spearman, an ordinary
 * least-squares line, and R². No multiple regression, no p-values, no model
 * selection. With a few thousand zones almost any relationship clears a
 * significance test, so a p-value here would launder a shrug into a finding;
 * what actually matters is the size of the effect and whether the shape holds
 * when you change the geography.
 */

import { euro, num, pct } from "./format";

export type Level = "departement" | "commune" | "iris";

export const LEVEL_LABELS: Record<Level, string> = {
  departement: "Departments",
  commune: "Cities",
  iris: "Neighbourhoods",
};

export const LEVEL_NOUN: Record<Level, string> = {
  departement: "department",
  commune: "city",
  iris: "neighbourhood",
};

/** Spelt out rather than suffixed, because "city" does not take a bare "s". */
export const LEVEL_PLURAL: Record<Level, string> = {
  departement: "departments",
  commune: "cities",
  iris: "neighbourhoods",
};

export interface Zone {
  code: string;
  name: string;
  dep: string;
  lon: number | null;
  lat: number | null;
  [measure: string]: string | number | null;
}

export interface StatsTable {
  level: Level;
  /** Column names present at this geography. */
  measures: string[];
  zones: Zone[];
}

export interface Measure {
  id: string;
  label: string;
  /** Axis caption, carrying the unit. */
  axis: string;
  format: (v: number) => string;
  /** Grouping in the picker. */
  group: string;
  /** Why this is worth plotting, as a tooltip. */
  hint?: string;
  /**
   * Plot on a log scale by default.
   *
   * Prices and densities span two orders of magnitude across this region, and
   * on a linear axis the entire outer ring collapses into one corner while a
   * handful of Paris arrondissements set the scale.
   */
  log?: boolean;
  /**
   * Which direction is better for someone living there.
   *
   * `-1` means less is better, `1` means more is better. **Omitted where there
   * is no honest answer**, and that is not laziness — a third of these measures
   * genuinely cut both ways, and colouring them anyway would be the app
   * asserting a preference it has no business having. `neutralWhy` says which
   * of the two it is: a measure with no bearing on living somewhere, or one
   * that is real but points in both directions at once.
   */
  valence?: 1 | -1;
  neutralWhy?: string;
}

const pct1 = (v: number) => `${(v * 100).toFixed(1)}%`;
const round0 = (v: number) => num(Math.round(v));
const one = (v: number) => v.toFixed(1);

/**
 * Everything plottable, in the order a reader would look for it.
 *
 * A measure listed here need not exist at every geography — `measuresFor`
 * filters to what the fetched table actually carries.
 */
export const MEASURES: Measure[] = [
  {
    id: "price_m2", label: "Price", axis: "median €/m²", group: "Prices",
    format: (v) => euro(Math.round(v)), log: true,
    hint: "Median €/m² for flats in the latest year with enough recorded sales.",
    neutralWhy: "cheaper is better to buy, dearer usually means better served — the same number, read two ways"
  },
  {
    id: "price_trend", label: "Price trend", axis: "price trend, %/yr", group: "Prices",
    format: pct,
    hint: "Least squares through log(median) across 2021–2025 — the same fit the Scenario pane uses.",
    neutralWhy: "good if you already own here, bad if you are trying to"
  },
  {
    id: "sales", label: "Sales", axis: "recorded sales", group: "Prices",
    format: round0, log: true,
    hint: "How much evidence the price is built on. Zones under five sales in a year are suppressed.",
    neutralWhy: "how much evidence the price rests on, not a quality of the place"
  },

  {
    id: "heat_day", label: "Heat island, day", axis: "daytime heat island score", group: "Climate",
    format: one,
    hint: "Area-weighted mean of the modelled daytime heat hazard over the zone's blocks.",
    valence: -1
  },
  {
    id: "heat_night", label: "Heat island, night", axis: "night-time heat island score", group: "Climate",
    format: one,
    hint: "Night-time heat is stored heat, and it tracks density more tightly than anything else here.",
    valence: -1
  },
  {
    id: "tropical_nights", label: "Tropical nights", axis: "nights a year above 20 °C", group: "Climate",
    format: one, hint: "Reference period 1976–2005, from the 8 km model grid.",
    valence: -1
  },
  {
    id: "tropical_nights_2055", label: "Tropical nights, 2055", axis: "nights a year above 20 °C, 2041–2070",
    group: "Climate", format: one, hint: "Under RCP 4.5. A scenario, not a forecast.",
    valence: -1
  },
  {
    id: "mean_temp", label: "Mean temperature", axis: "mean temperature, °C", group: "Climate",
    format: (v) => `${v.toFixed(1)}°`,
    valence: -1
  },

  {
    id: "green_score", label: "Green index", axis: "green index, 0–100", group: "Environment",
    format: one,
    valence: 1
  },
  {
    id: "noise", label: "Noise", axis: "noise class, 1–3", group: "Environment",
    format: one, valence: -1,
    hint: "1 is within WHO guidelines, 3 above EU limit values.",
  },
  {
    id: "air", label: "Air quality", axis: "air class, 1–3", group: "Environment",
    format: one, valence: -1,
    hint: "NO₂ exposure class. 1 is within WHO guidelines, 3 above EU limit values.",
  },

  {
    id: "clay", label: "Clay exposure", axis: "clay exposure, 1–3", group: "Risks",
    format: one,
    hint: "Mean over the mapped ground in the zone. Read it with clay cover, not alone.",
    valence: -1,
  },
  {
    id: "clay_cover", label: "Clay cover", axis: "share of the zone with mapped clay", group: "Risks",
    format: pct1,
    valence: -1
  },
  {
    id: "catnat_flood", label: "Flood declarations", axis: "flood declarations since 1982",
    group: "Risks", format: round0,
    valence: -1
  },
  {
    id: "catnat_drought", label: "Drought declarations", axis: "drought declarations since 1982",
    group: "Risks", format: round0,
    valence: -1
  },
  {
    id: "catnat_total", label: "All declarations", axis: "disaster declarations since 1982",
    group: "Risks", format: round0,
    valence: -1
  },
  {
    id: "fire_ha", label: "Hectares burned", axis: "hectares burned since 2006", group: "Risks",
    format: one,
    hint: "Counts track reporting effort; area burned is the series that survives that.",
    valence: -1
  },

  {
    id: "density", label: "Density", axis: "people per km²", group: "People",
    format: round0, log: true,
    neutralWhy: "density buys transit, shops and walkability, and costs quiet, space and cool nights"
  },
  {
    id: "pop", label: "Population", axis: "population", group: "People",
    format: round0, log: true,
    neutralWhy: "the size of a place, not a quality of it",
  },
  {
    id: "growth_rate", label: "Population change", axis: "population change, %/yr",
    group: "People", format: pct,
    neutralWhy: "growth is vitality or pressure depending on whether the housing followed",
  },
  {
    id: "migration_rate", label: "Net migration", axis: "net migration, %/yr",
    group: "People", format: pct,
    neutralWhy: "people arriving is a vote of confidence and a source of competition"
  },
  {
    id: "vacancy_rate", label: "Vacant homes", axis: "vacant homes, % of stock",
    group: "People", format: pct1,
    neutralWhy: "slack that keeps prices down, or a symptom of a place emptying"
  },
  {
    id: "second_rate", label: "Second homes", axis: "second homes, % of stock",
    group: "People", format: pct1,
    neutralWhy: "a sign a place is worth visiting, and of neighbours who are rarely there"
  },
  {
    id: "hh_size", label: "Household size", axis: "people per household", group: "People",
    format: one,
    neutralWhy: "who lives here, not how good it is",
  },

  {
    id: "tax_rate", label: "Taxe foncière rate", axis: "taxe foncière rate", group: "Cost",
    format: pct1,
    hint: "The commune's voted rate, exact from the DGFiP REI file.",
    neutralWhy: "who lives here, not how good it is",
    valence: -1
  },
];

export const MEASURE_BY_ID: Record<string, Measure> = Object.fromEntries(
  MEASURES.map((m) => [m.id, m]),
);

/**
 * The direction that is better to live in, as a phrase.
 *
 * Null where the measure has no such direction — the caller has to say so
 * rather than defaulting to one.
 */
export function betterDirection(measure: Measure): string | null {
  if (!measure.valence) return null;
  // The axis caption carries its range or unit after a comma — fine above an
  // axis, clumsy mid-sentence ("higher green index, 0–100 and lower …").
  const noun = measure.axis.split(",")[0].trim();
  return `${measure.valence > 0 ? "higher" : "lower"} ${noun}`;
}

/** The measures that exist at this geography, in catalogue order. */
export function measuresFor(table: StatsTable | null): Measure[] {
  if (!table) return [];
  const present = new Set(table.measures);
  return MEASURES.filter((m) => present.has(m.id));
}

// ---------------------------------------------------------------------------
// The models
// ---------------------------------------------------------------------------

export function pearson(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 3) return null;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0, syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  const d = Math.sqrt(sxx * syy);
  return d === 0 ? null : sxy / d;
}

/** Fractional ranks, ties averaged — what makes Spearman robust to outliers. */
export function ranks(values: number[]): number[] {
  const order = values.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
  const out: number[] = Array.from({ length: values.length }, () => 0);
  let i = 0;
  while (i < order.length) {
    let j = i;
    while (j + 1 < order.length && order[j + 1].v === order[i].v) j++;
    const mean = (i + j) / 2;
    for (let k = i; k <= j; k++) out[order[k].i] = mean;
    i = j + 1;
  }
  return out;
}

export interface Point {
  code: string;
  name: string;
  dep: string;
  x: number;
  y: number;
  lon: number | null;
  lat: number | null;
}

export interface Fit {
  n: number;
  /** Linear correlation. Null when fewer than three zones have both measures. */
  r: number | null;
  /** Rank correlation — the one to trust when either measure is skewed. */
  rho: number | null;
  /** Ordinary least squares, in the plotted (possibly log) space. */
  slope: number | null;
  intercept: number | null;
  r2: number | null;
}

/**
 * Zones with both measures, dropped otherwise.
 *
 * Dropping is the only honest option — a zone whose sales were suppressed has
 * no price, not a price of zero — but it does mean the sample changes with the
 * axes, which is why `n` is always on screen next to the correlation.
 */
export function pairs(
  zones: Zone[],
  x: string,
  y: string,
  filter?: (z: Zone) => boolean,
): Point[] {
  const out: Point[] = [];
  for (const z of zones) {
    if (filter && !filter(z)) continue;
    const xv = z[x];
    const yv = z[y];
    if (typeof xv !== "number" || typeof yv !== "number") continue;
    if (!Number.isFinite(xv) || !Number.isFinite(yv)) continue;
    out.push({
      code: z.code, name: z.name, dep: z.dep,
      x: xv, y: yv, lon: z.lon, lat: z.lat,
    });
  }
  return out;
}

/** Log10, guarding the non-positive values a log axis cannot represent. */
export const toLog = (v: number): number | null => (v > 0 ? Math.log10(v) : null);

/**
 * Can this set of values go on a log axis at all?
 *
 * Several measures here are signed — the heat-island score is an anomaly, and
 * the outer departments run negative — and a log scale drops every one of those
 * points. Dropping them *silently* is the failure mode worth designing out: the
 * plot still looks fine, the correlation still computes, and a third of the
 * region has quietly left the sample.
 */
export function canLog(values: number[]): boolean {
  return values.length > 0 && values.every((v) => v > 0);
}

export function fit(points: Point[], logX: boolean, logY: boolean): Fit {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const p of points) {
    const x = logX ? toLog(p.x) : p.x;
    const y = logY ? toLog(p.y) : p.y;
    if (x === null || y === null) continue;
    xs.push(x);
    ys.push(y);
  }

  const n = xs.length;
  const r = pearson(xs, ys);
  const rho = n >= 3 ? pearson(ranks(xs), ranks(ys)) : null;
  if (n < 3 || r === null) {
    return { n, r, rho, slope: null, intercept: null, r2: null };
  }

  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) {
    sxy += (xs[i] - mx) * (ys[i] - my);
    sxx += (xs[i] - mx) ** 2;
  }
  const slope = sxx === 0 ? null : sxy / sxx;
  return {
    n, r, rho,
    slope,
    intercept: slope === null ? null : my - slope * mx,
    r2: r * r,
  };
}

/** How strong a correlation is, in words rather than a bare number. */
export function strength(r: number | null): string {
  if (r === null) return "no fit";
  const a = Math.abs(r);
  if (a < 0.1) return "no relationship";
  if (a < 0.3) return "weak";
  if (a < 0.5) return "moderate";
  if (a < 0.7) return "strong";
  return "very strong";
}

/**
 * Does the relationship survive changing the geography?
 *
 * This is the check that matters most and the one nobody runs. Aggregating the
 * same places into bigger zones can change a correlation's size and even its
 * sign — green space against price is genuinely negative across neighbourhoods
 * and positive across departments in this region — because a correlation
 * between area averages is a statement about areas, not about the flats inside
 * them. Rather than bury that in a footnote, the panel computes all three
 * geographies and says out loud when they disagree.
 */
export interface LevelAgreement {
  byLevel: { level: Level; r: number | null; n: number }[];
  /** True when the sign is not the same everywhere it can be computed. */
  signFlips: boolean;
  /** True when the strength label differs across geographies. */
  strengthVaries: boolean;
}

export function agreement(
  tables: Partial<Record<Level, StatsTable>>,
  x: string,
  y: string,
  logX: boolean,
  logY: boolean,
): LevelAgreement {
  const byLevel: { level: Level; r: number | null; n: number }[] = [];
  for (const level of ["iris", "commune", "departement"] as Level[]) {
    const table = tables[level];
    if (!table || !table.measures.includes(x) || !table.measures.includes(y)) continue;
    const f = fit(pairs(table.zones, x, y), logX, logY);
    byLevel.push({ level, r: f.r, n: f.n });
  }

  const rs = byLevel.map((b) => b.r).filter((v): v is number => v !== null);
  const signs = new Set(rs.filter((v) => Math.abs(v) >= 0.1).map((v) => Math.sign(v)));
  const labels = new Set(rs.map((v) => strength(v)));
  return {
    byLevel,
    signFlips: signs.size > 1,
    strengthVaries: labels.size > 1,
  };
}
